import React from "react";
import { Player, PlayerStats, Game, PlayerStats2 } from "../../types/types";
import styled from "@emotion/styled";
import { Paper, Tooltip, useTheme } from "@mui/material";
import { TeamLogos, TeamsList, TOOLTIPS } from "../../globals/Globals";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import PlayerCardPopover from "./PlayerCardPopover";
import { IMAGES_BUCKET } from "../../configs/consts";
import { CURRENT_SEASON } from "../../globals/BBLData";

export interface Props {
  player: Player;
  stats?: PlayerStats;
  width: string;
  onPlusClick: (player: Player) => void;
  onNavClick: (player: Player) => void;
  removeIcon?: boolean;
  extraStat: {
    label: string;
    value: string | number;
    tooltipText: string;
  };
  extraStats?: {
    label: string;
    value: string | number;
    tooltipText: string;
  }[];
}

const CardNumber = styled.b`
  font-size: 1rem;
  @media (min-width: 1024px) {
    font-size: 1.1rem;
  }
`;

const CardLabel = styled.span`
  font-size: 0.75rem;
  line-height: 1;
  @media (min-width: 1024px) {
    font-size: 0.9rem;
  }
`;

function PlayerCardExtraStat({
  player,
  stats,
  width,
  onPlusClick,
  onNavClick,
  removeIcon,
  extraStat,
  extraStats = [],
}: Props) {
  const theme = useTheme();
  if (!player.stats[0].lastgame) {
    return null;
  }

  let lastgame: Game = player.stats[0].lastgame;

  let stat: PlayerStats2 = player.stats[0];
  player.stats.map(function (thestat) {
    if (thestat.season === CURRENT_SEASON) {
      stat = thestat;
      if (thestat.lastgame) {
        lastgame = thestat.lastgame;
      }
    }
  });
  const team = lastgame?.team?.name ? lastgame.team.name : "";
  const colors = TeamsList[team] ? TeamsList[team] : ["", ""];
  const playerimg = `${IMAGES_BUCKET}${player.firstname}-${player.lastname}.png`;
  const teamLogo = TeamLogos[team] ? TeamLogos[team] : "";
  //Todo Implement increase logic
  const increase = stat.price_change > 0;
  const pricestring = stat.price.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });

  let CardItems = [
    {
      label: "PTS",
      value: lastgame.fiveRoundAverage,
      tooltip: TOOLTIPS["PTS"],
    },
    {
      label: "3Ra",
      value: lastgame.threeRoundAverage,
      tooltip: TOOLTIPS["3ra"],
    },
    { label: "BE", value: lastgame.scbe, tooltip: TOOLTIPS["be"] },
  ];
  if (extraStat) {
    CardItems.unshift({
      label: extraStat.label,
      //@ts-ignore
      value: extraStat.value,
      tooltip: extraStat.tooltipText,
    });
  }
  if (extraStats.length > 0) {
    extraStats.map(function (exs) {
      CardItems.push({
        label: exs.label,
        value: exs.value,
        tooltip: exs.tooltipText,
      });
    });
  }

  return (
    <Paper
      style={{
        width: width,
        minWidth: width,
        position: "relative",
        textAlign: "center",
      }}
      className="playercard"
      elevation={3}
    >
      <a href={`/player/${player.id}`}>
        <div
          style={{
            background: `linear-gradient(120deg, ${colors[0]} 0%, ${colors[0]} 50%, ${colors[1]} 50%)`,
            borderTopRightRadius: 4,
            borderTopLeftRadius: 4,
          }}
        >
          <div
            style={{
              backgroundImage: `url("${playerimg}")`,
              backgroundSize: "contain",
              backgroundPosition: "center center",
              paddingBottom: "60%",
              backgroundRepeat: "no-repeat",
              //backgroundColor: "blue",
              borderTopRightRadius: 4,
              borderTopLeftRadius: 4,
            }}
          >
            <div style={{ position: "absolute", top: 6, left: 6 }}>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "flex-start",
                  alignItems: "flex-start",
                }}
              >
                {/* <span style={{color: "white"}}>{player.stats[0]?.last5Games[0]?.team?.name}</span> */}
                <img
                  style={{ width: "30px", height: "30px" }}
                  src={teamLogo}
                  alt={team}
                />
              </div>
            </div>
          </div>
        </div>
      </a>
      <Paper>
        <PlayerCardPopover player={player} />

        <a href={`/player/${player.id}`}>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              marginTop: "0.5rem",
              marginBottom: "0.5rem",
            }}
          >
            <Tooltip
              title={`${player.firstname} ${player.lastname}`}
              placement="top"
            >
              <span>
                <b
                  style={{
                    color: theme.palette.mode === "dark" ? "white" : "black",
                  }}
                >
                  {player.firstname[0]}.{player.lastname}
                </b>
              </span>
            </Tooltip>
            <Tooltip
              title={`SC Price of ${player.firstname} ${player.lastname} this round`}
            >
              <span
                style={{
                  display: "flex",
                  width: "100%",
                  justifyContent: "center",
                  marginTop: "0.25rem",
                }}
              >
                <b style={{ color: increase ? "green" : "red" }}>
                  {pricestring}
                </b>
                {increase ? (
                  <TrendingUpIcon
                    style={{ color: "green", marginLeft: "0.25rem" }}
                  />
                ) : (
                  <TrendingDownIcon
                    style={{ color: "red", marginLeft: "0.5rem" }}
                  />
                )}
              </span>
            </Tooltip>
          </div>
        </a>
        <div
          style={{
            display: "grid",
            gridTemplateRows: "1fr 1fr",
            gridTemplateColumns: "1fr 1fr 1fr",
            gridRowGap: "0.5rem",
            marginBottom: "1rem",
          }}
        >
          {CardItems.map(function (item) {
            return (
              <Tooltip title={item.tooltip} placement="top">
                <div style={{ display: "flex", flexDirection: "column" }}>
                  <div>
                    <CardNumber>{item.value}</CardNumber>
                  </div>
                  <CardLabel>{item.label}</CardLabel>
                </div>
              </Tooltip>
            );
          })}
        </div>
      </Paper>
    </Paper>
  );
}

export default PlayerCardExtraStat;
